import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Clock, ChevronDown, ChevronRight, FileText, Send, CheckCircle, Ban, RotateCcw, XCircle } from 'lucide-react';
import { HISTORY_COLORS } from './constants';

const historyIconMap = {
  request_created: Send, request_reviewed: CheckCircle, request_cancelled: Ban,
  cancellation_requested: RotateCcw, cancellation_rejected: XCircle,
};

export function ReviewNotesCard({ items, internalNote }) {
  const { t } = useTranslation();
  const noted = items?.filter(i => i.reviewer_note) || [];
  if (noted.length === 0 && !internalNote) return null;

  return (
    <Card className="bg-white border-gray-200/80 shadow-none">
      <CardHeader className="pb-2"><CardTitle className="text-xs text-gray-400 font-medium uppercase tracking-wider">{t('review_notes')}</CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {noted.map(i => (
          <div key={i.id} className="text-sm"><span className="font-medium text-gray-800">{i.right_name}:</span> <span className="text-gray-500">{i.reviewer_note}</span></div>
        ))}
        {internalNote && <><Separator className="my-2 bg-gray-100" /><p className="text-sm"><span className="font-medium text-gray-800">{t('internal')}:</span> <span className="text-gray-500">{internalNote}</span></p></>}
      </CardContent>
    </Card>
  );
}

export function HistoryCard({ history }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!history?.length) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="bg-white border-gray-200/80 shadow-none">
        <CollapsibleTrigger asChild>
          <button data-testid="toggle-history-btn" className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors rounded-t-lg">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-gray-400" />
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider">{t('change_history')}</span>
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-gray-200 text-gray-400">{history.length}</Badge>
            </div>
            {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-4">
            <div className="relative pl-5 border-l-2 border-gray-100 space-y-0">
              {history.map((h) => {
                const Icon = historyIconMap[h.action] || FileText;
                const color = HISTORY_COLORS[h.action] || 'text-gray-400 bg-gray-50';
                const ts = h.created_at?.replace('T', ' ').split('.')[0];
                return (
                  <div key={h.id} className="relative pb-4 last:pb-0">
                    <div className={`absolute -left-[calc(0.5rem+1.5px)] top-0.5 w-4 h-4 rounded-full flex items-center justify-center ${color}`}>
                      <Icon size={9} strokeWidth={2.5} />
                    </div>
                    <div className="ml-3">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">{h.action?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                        <span className="text-[11px] text-gray-400">{h.user_name}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{h.details}</p>
                      <p className="text-[10px] text-gray-300 mt-0.5">{ts}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
